import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../shell/PageHeader';
import { DataTable, KpiTile, Notice, StatusPill, TabBar, TabPanel } from '../patterns';
import type { StatusVariant } from '../patterns';
import NutritionInventoryMovements from './NutritionInventoryMovements';
import NutritionInventoryOrders from './NutritionInventoryOrders';
import {
  getInventoryStock,
  getOrderToNeed,
  createOrder,
} from '../../services/nutritionService';
import type {
  FertilizerStockRow,
  FertilizerStock,
  OrderToNeed,
  OrderToNeedLine,
  OrderToNeedSeason,
} from '../../services/nutritionService';
import ReceiveStockModal from './ReceiveStockModal';
import { formatMeasure } from '../../utils/formatMeasure';
import { formatDate } from '../../dateFormat';
import './nutrition.css';

const dwellVariant = (status: string): StatusVariant =>
  status === 'move' ? 'danger' : status === 'low' ? 'warn' : 'ok';

const DwellBar: React.FC<{ row: FertilizerStockRow }> = ({ row }) => {
  const { t } = useTranslation();

  // #534: a wholly absent `dwell` (no open lot at all) and a `dwell.days` of
  // null (an open lot with no measured dwell) are BOTH unmeasured. Either used
  // to set pct = 0 and draw a 0%-width bar (days == null) or a bare em-dash
  // (`!row.dwell`) — a 0%-width bar is pixel-identical to a product measured
  // at zero days, and an em-dash gives no indication the figure was never
  // taken at all. Both now get the identical not-measured treatment.
  // #541: a measured zero is not an unmeasured dwell. "Not measured" would name
  // a cause the code did not test for — the product WAS measured, at zero.
  if (row.dwellReason === 'no_stock') {
    return (
      <span className="ntr-dwell-unknown" data-testid="dwell-no-stock">
        {t('nutrition.inventory.dwellNoStock')}
      </span>
    );
  }

  if (!row.dwell || row.dwell.days == null) {
    return (
      <span className="ntr-dwell-unknown" data-testid="dwell-not-measured">
        {t('nutrition.inventory.dwellNotMeasured')}
      </span>
    );
  }

  const { days, limit, status } = row.dwell;
  const pct = Math.min(100, Math.round((days / limit) * 100));
  return (
    <div className={`ntr-dwell ntr-dwell--${status}`}>
      <div className="ntr-dwell-track">
        <div className="ntr-dwell-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="ntr-dwell-figure">{t('nutrition.inventory.dwellDays', { days, limit })}</span>
      <StatusPill variant={dwellVariant(status)}>
        {t(`nutrition.inventory.status.${status}`)}
      </StatusPill>
    </div>
  );
};

/**
 * The badge for a figure-less line. `conflictReason: 'unknown_product'` is NOT a
 * unit problem: `backend/src/utils/soilDemand.js` emits it when a SoilProgram
 * names a product that is absent from the Fertilizer catalogue altogether. The
 * unit-conflict badge would send the manager to fix a stock unit on a product
 * that does not exist (and `inventory.unitConflict.reason` carries no
 * `unknown_product` key), so that case gets its own badge and its own
 * remediation.
 */
const ConflictBadge: React.FC<{ reason?: string }> = ({ reason }) => {
  const { t } = useTranslation();
  return (
    <StatusPill variant="warn">
      {reason === 'unknown_product'
        ? t('inventory.unknownProduct.badge')
        : t('inventory.unitConflict.badge')}
    </StatusPill>
  );
};

const coverPct = (row: FertilizerStockRow, orderLines: OrderToNeedLine[]): string => {
  if (row.onHand == null) return '—';
  const line = orderLines.find((l) => l.productId === row.productId);
  if (!line || line.demand <= 0) return '—';
  return `${Math.min(100, Math.round((row.onHand / line.demand) * 100))}%`;
};

const TAB_STOCK = 'stock';
const TAB_MOVEMENTS = 'movements';
const TAB_ORDERS = 'orders';

const pathToTab = (pathname: string): string => {
  if (pathname.endsWith('/movements')) return TAB_MOVEMENTS;
  if (pathname.endsWith('/orders')) return TAB_ORDERS;
  return TAB_STOCK;
};

/**
 * Returns only the seasons/rounds that have at least one line worth showing:
 * either a real order need (orderQty > 0) or a unit-conflicted line (Slice 4,
 * Decision B) — a conflict has no trustworthy figure but must still be LISTED
 * for review, never silently dropped.
 */
const filterSeasonsToNeed = (seasons: OrderToNeedSeason[]): OrderToNeedSeason[] =>
  seasons
    .map((s) => ({
      ...s,
      rounds: s.rounds
        .map((r) => ({
          ...r,
          lines: r.lines.filter((l) => (l.orderQty ?? 0) > 0 || l.status === 'conflict'),
        }))
        .filter((r) => r.lines.length > 0),
    }))
    .filter((s) => s.rounds.length > 0);

const NutritionInventoryPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = pathToTab(location.pathname);
  const [stock, setStock] = useState<FertilizerStock | null>(null);
  const [orderData, setOrderData] = useState<OrderToNeed | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [showReceive, setShowReceive] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [selectedRounds, setSelectedRounds] = useState<Set<string>>(new Set());

  const handleTabSelect = (key: string) => {
    if (key === TAB_MOVEMENTS) navigate('/nutrition/inventory/movements');
    else if (key === TAB_ORDERS) navigate('/nutrition/inventory/orders');
    else navigate('/nutrition/inventory');
  };

  const load = useCallback(async () => {
    try {
      const [s, o] = await Promise.all([getInventoryStock(), getOrderToNeed()]);
      setStock(s);
      setOrderData(o);
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleRound = (key: string) => {
    setSelectedRounds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleCreateOrder = async () => {
    if (!orderData) return;
    const seasons = filterSeasonsToNeed(orderData.seasons ?? []);

    // Collect all rounds whose selection key is in selectedRounds
    const selectedRoundObjects = seasons.flatMap((s) =>
      s.rounds
        .filter((r) => selectedRounds.has(`${s.programmeId}:${r.roundId}`))
        .map((r) => ({ ...r, programmeId: s.programmeId }))
    );

    if (!selectedRoundObjects.length) return;

    // Sum orderQty per productId across selected rounds.
    // Unit-conflicted lines (Slice 4, Decision B) carry orderQty: null and MUST be
    // excluded here — they are listed figure-less in the UI for review, never
    // summed (never `0`, which would silently under/over-order) and never
    // `null` (which would poison the running total into NaN).
    const lineMap = new Map<string, { productId: string; productName?: string; qty: number; unit: string }>();
    for (const round of selectedRoundObjects) {
      for (const line of round.lines) {
        // A null productId is an unresolvable soil product name (soilDemand.js
        // `unknown_product`). It always arrives as `status: 'conflict'` too, but
        // the check is spelled out rather than inferred: an order line is
        // addressed by productId, so a line without one cannot be ordered.
        if (line.status === 'conflict' || line.orderQty == null || line.productId == null) continue;
        const existing = lineMap.get(line.productId);
        if (existing) {
          existing.qty += line.orderQty;
        } else {
          lineMap.set(line.productId, {
            productId: line.productId,
            productName: line.productName,
            qty: line.orderQty,
            unit: line.unit,
          });
        }
      }
    }

    const lines = Array.from(lineMap.values());
    if (!lines.length) return;

    // orderBy = earliest non-null orderBy among selected rounds
    const orderByCandidates = selectedRoundObjects
      .map((r) => r.orderBy)
      .filter((d): d is string => d !== null);
    const orderBy = orderByCandidates.length > 0
      ? orderByCandidates.sort()[0]
      : null;

    setOrdering(true);
    try {
      await createOrder({ lines, orderBy });
      await load();
      setSelectedRounds(new Set());
    } finally {
      setOrdering(false);
    }
  };

  const kpis = stock?.kpis;
  const rows = stock?.rows ?? [];
  const remaining = orderData?.remaining ?? [];
  const orderLines = remaining.filter((l) => (l.orderQty ?? 0) > 0);
  const stockCover = orderData?.stockCover;

  const sources = orderData?.sources ?? [];
  const unmeasured = sources.filter((s) => s.status === 'not_computed');
  // Whether ANY source actually reported. This gates the "nothing needs
  // ordering" copy, which is a MEASURED claim: it may only be shown on the back
  // of a real measurement. A response with no `sources` block at all is an older
  // server, which always measured — treat it as measured rather than silently
  // withholding the empty state from it.
  const hasMeasuredSource = sources.length === 0 || sources.some((s) => s.status !== 'not_computed');

  const visibleSeasons = filterSeasonsToNeed(orderData?.seasons ?? []);

  /*
   * #536: order-to-need sums EVERY active programme — one stock pool, one
   * purchase decision. That is pre-existing and deliberate, but it is invisible:
   * two programmes carrying identically-named rounds read as one plan shown
   * twice rather than as double demand.
   *
   * A programme becomes active as a SIDE EFFECT of creating a session
   * (nutritionSessionService.js, "Auto-lock programme to 'active'"), and nothing
   * supersedes the one already active — so they accumulate. That lifecycle is
   * deliberately NOT changed here: a tenant legitimately running two concurrent
   * programmes would lose that, and whether this page should span all active
   * programmes belongs with the Nutrition Overview redesign (#443). This names
   * what is contributing; it does not decide what should.
   *
   * Read from `visibleSeasons`, not the raw payload: a programme whose demand
   * is already fully covered by stock is filtered out of the list below (every
   * line's orderQty <= 0), and it is not contributing to what needs ordering —
   * naming it here would assert demand for a programme the page shows nothing
   * for.
   */
  const contributing = Array.from(
    new Map(visibleSeasons.map((s) => [s.programmeId, s.season])).entries()
  );

  // Derive the set of lines the current selection would produce (for disable logic)
  const selectionHasLines = (() => {
    if (selectedRounds.size === 0) return false;
    for (const season of visibleSeasons) {
      for (const round of season.rounds) {
        const key = `${season.programmeId}:${round.roundId}`;
        if (selectedRounds.has(key) && round.lines.length > 0) return true;
      }
    }
    return false;
  })();

  return (
    <div className="ntr-page" data-testid="nutrition-inventory">
      <PageHeader
        title={t('nutrition.inventory.title')}
        meta={t('nutrition.inventory.meta')}
        actions={
          activeTab === TAB_STOCK ? (
            <>
              <button
                type="button"
                className="pat-btn"
                onClick={() => setShowReceive(true)}
              >
                {t('nutrition.inventory.receiveStock')}
              </button>
              <button
                type="button"
                className="pat-btn pat-btn--primary"
                onClick={handleCreateOrder}
                disabled={ordering || !selectionHasLines}
              >
                {t('nutrition.inventory.createOrder')}
              </button>
            </>
          ) : undefined
        }
      />

      <TabBar activeKey={activeTab} onSelect={handleTabSelect}>
        <TabPanel id={TAB_STOCK} title={t('nutrition.inventory.tab.stock')}>
          {loadError && (
            <Notice tone="danger">{t('common.loadError', 'Could not load inventory data.')}</Notice>
          )}

          <div className="pat-kpi-row">
            <KpiTile
              label={t('nutrition.inventory.kpi.onHandValue')}
              value={kpis != null ? `R${kpis.onHandValue.toFixed(0)}` : '—'}
            />
            <KpiTile
              label={t('nutrition.inventory.kpi.dwellBreaches')}
              value={kpis != null ? String(kpis.dwellBreaches) : '—'}
              delta={kpis != null && kpis.dwellLow > 0
                ? `${kpis.dwellLow} ${t('nutrition.inventory.approaching')}`
                : undefined}
            />
            {/*
              This tile counts distinct PRODUCTS whose orderQty > 0. That is a
              different figure from the dashboard's order alert (which counts
              demand RECORDS needing an order) and from roundCount (all remaining
              records). Three counts live on this one feature, so the tile says
              which one it is rather than showing a bare integer.
            */}
            <KpiTile
              label={t('nutrition.inventory.kpi.orderToNeed')}
              value={orderLines.length > 0 ? String(orderLines.length) : '—'}
              delta={orderLines.length > 0
                ? t('nutrition.inventory.kpi.orderToNeedUnit', { count: orderLines.length })
                : undefined}
            />
            <KpiTile
              label={t('nutrition.inventory.kpi.stockCover')}
              // Gated on whether a figure EXISTS, not on whether every source
              // reported. A foliar-only tenant has no SoilProgram (soil reads
              // `not_computed` as its ordinary state) yet its foliar cover was
              // genuinely measured — blanking it to "Not measured" discards a
              // real measurement. What the figure excludes is stated by the
              // unmeasured-source warning below. "Not measured" is reserved for
              // the case where no figure was produced at all.
              value={stockCover != null
                ? `${stockCover}%`
                : unmeasured.length > 0
                  ? t('nutrition.inventory.kpi.notMeasured')
                  : '—'}
            />
          </div>

          <div className="ntr-inv-split">
            <DataTable<FertilizerStockRow>
              rows={rows}
              rowKey={(r) => r.productId}
              columns={[
                {
                  key: 'product',
                  label: t('nutrition.inventory.col.product'),
                  render: (r) => <strong>{r.productName}</strong>,
                },
                {
                  key: 'onHand',
                  label: t('nutrition.inventory.col.onHand'),
                  align: 'right',
                  render: (r) => (r.stockStatus === 'conflict' || r.onHand == null)
                    ? <StatusPill variant="warn">{t('inventory.unitConflict.badge')}</StatusPill>
                    : formatMeasure(r.onHand, r.unit),
                },
                {
                  key: 'dwell',
                  label: t('nutrition.inventory.col.dwell'),
                  render: (r) => <DwellBar row={r} />,
                },
                {
                  key: 'cover',
                  label: t('nutrition.inventory.col.cover'),
                  align: 'right',
                  render: (r) => coverPct(r, remaining),
                },
              ]}
              emptyState={t('nutrition.inventory.empty')}
              mobileCardRow={(r) => (
                <div>
                  <strong>{r.productName}</strong>
                  <div style={{ fontSize: 12, color: 'var(--shell-text-muted)' }}>
                    {r.stockStatus === 'conflict' || r.onHand == null
                      ? <StatusPill variant="warn">{t('inventory.unitConflict.badge')}</StatusPill>
                      : formatMeasure(r.onHand, r.unit)}
                    {' · '}<DwellBar row={r} />
                  </div>
                </div>
              )}
            />

            <div>
              <div className="ntr-otn-card">
                <p className="ntr-otn-card__title">{t('nutrition.inventory.orderToNeed.title')}</p>

                {contributing.length > 1 && (
                  <p className="ntr-otn-contributing" data-testid="otn-contributing">
                    {t('nutrition.inventory.orderToNeed.contributing', {
                      count: contributing.length,
                      names: contributing.map(([, season]) => season).join(', '),
                    })}
                  </p>
                )}

                {/*
                  The warning and the demand list are INDEPENDENT. They used to
                  be two arms of one ternary, which was safe only while `sources`
                  moved all-or-nothing. It does not: the backend reports soil as
                  `not_computed` for every tenant that never adopted the soil
                  module, so a foliar-only tenant — precisely the tenant #528 was
                  reopened for — got the warning INSTEAD of its season list,
                  checkboxes and Create Order button. Partial coverage stated
                  honestly beats an all-or-nothing blank (spec §3).
                */}
                {unmeasured.length > 0 && (
                  <div className="ntr-otn-warning" data-testid="otn-not-computed">
                    <p>{t('nutrition.inventory.orderToNeed.notComputed')}</p>
                    <ul>
                      {unmeasured.map((s) => (
                        <li key={s.module}>
                          {s.module === 'foliar'
                            ? t('nutrition.inventory.orderToNeed.notComputedFoliar')
                            : t('nutrition.inventory.orderToNeed.notComputedSoil')}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {visibleSeasons.length === 0 ? (
                  // The empty copy is withheld when NOTHING was measured: with no
                  // measurement behind it, "nothing needs ordering" is an unknown
                  // rendered as a zero, and the warning above stands alone.
                  hasMeasuredSource ? (
                    <p className="ntr-otn-empty">{t('nutrition.inventory.orderToNeed.empty', 'Nothing needs ordering')}</p>
                  ) : null
                ) : (
                  <>
                    <p className="ntr-otn-hint">{t('nutrition.inventory.orderToNeed.selectHint', 'Select what to include in your order')}</p>
                    {visibleSeasons.map((season) => (
                      <div key={`${season.programmeId}:${season.season}`} className="ntr-otn-season">
                        <p className="ntr-otn-season__label">{season.season}</p>
                        {season.rounds.map((round) => {
                          const key = `${season.programmeId}:${round.roundId}`;
                          const checked = selectedRounds.has(key);
                          return (
                            <div key={key} className="ntr-otn-round">
                              <label className="ntr-otn-round__row">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleRound(key)}
                                  className="ntr-otn-round__check"
                                />
                                <span className="ntr-otn-round__name">{round.name}</span>
                                {round.source && (
                                  <span data-testid={`otn-source-${round.roundId}`}>
                                    <StatusPill variant="info">
                                      {round.source === 'foliar'
                                        ? t('nutrition.inventory.orderToNeed.sourceFoliar')
                                        : t('nutrition.inventory.orderToNeed.sourceSoil')}
                                    </StatusPill>
                                  </span>
                                )}
                                <span className="ntr-otn-round__date">
                                  {/*
                                    Date-only cell, so it is formatted in UTC (#398): orderBy is
                                    derived from a round's plannedStart (or a soil month's first
                                    day) minus whole lead-time days, all midnight-UTC values. In a
                                    timezone behind UTC, formatting it locally would render the
                                    previous day and quietly move a purchasing deadline.
                                  */}
                                  {round.orderBy
                                    ? `${t('nutrition.inventory.orderBy')} ${formatDate(round.orderBy, { timeZone: 'UTC' })}`
                                    : (
                                      /*
                                       * #537: stock is allocated in plannedStart order, nulls LAST.
                                       * A bare em-dash hid both the cause and the consequence: an
                                       * undated round absorbs the shortfall however urgent it is.
                                       */
                                      <span className="ntr-otn-no-date" data-testid={`otn-no-date-${round.roundId}`}>
                                        {t('nutrition.inventory.orderToNeed.noPlannedStart')}
                                      </span>
                                    )}
                                </span>
                              </label>
                              <div className="ntr-otn-round__lines">
                                {round.lines.map((line, idx) => (
                                  // `unknown:${productName}` is a safe key because the backend dedupes
                                  // unresolved lines by normalised name + unit (nutritionOrderService.js
                                  // `demandKey`), so two null-productId lines with the same productName
                                  // cannot coexist in one record. `idx` covers only a nameless line.
                                  <div key={line.productId ?? `unknown:${line.productName ?? idx}`} className="ntr-otn-line">
                                    <span className="ntr-otn-line__name">{line.productName ?? line.productId}</span>
                                    {line.status === 'conflict' || line.orderQty == null
                                      ? <ConflictBadge reason={line.conflictReason} />
                                      : <span className="ntr-otn-line__qty">{formatMeasure(line.orderQty, line.unit)}</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </>
                )}
              </div>

              <Notice tone="danger" className="mt-3">
                {t('nutrition.inventory.whyJustInTime')}
              </Notice>
            </div>
          </div>

          <ReceiveStockModal
            show={showReceive}
            onHide={() => setShowReceive(false)}
            onReceived={load}
          />
        </TabPanel>
        <TabPanel id={TAB_MOVEMENTS} title={t('nutrition.inventory.tab.movements')}>
          <NutritionInventoryMovements />
        </TabPanel>
        <TabPanel id={TAB_ORDERS} title={t('nutrition.inventory.tab.orders')}>
          <NutritionInventoryOrders />
        </TabPanel>
      </TabBar>
    </div>
  );
};

export default NutritionInventoryPage;
